import { createContext, useMemo } from 'react';

import { usePwaInstallPrompt } from './hooks/usePwaInstallPrompt';
import { PwaPhaseGate } from './PwaPhaseGate';

const PwaContext = createContext(null);

/**
 * Shared PWA provider. Always exposes install-prompt state from
 * `usePwaInstallPrompt`. Pass `enablePhase` for LPs that show InstallPhase
 * (web); desktop/mobile omit it so phase hooks never mount.
 *
 * @param {object} props - Component props.
 * @param {string} props.storageKey - Base storage namespace for PWA values.
 * @param {boolean} [props.enablePhase=false] - When true, mounts {@link PwaPhaseGate}.
 * @param {import("react").ReactNode} props.children - Landing tree that reads install state through `PwaContext`.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`, plus the phase gate when enabled.
 */
const PwaProvider = ({ storageKey, children, enablePhase = false }) => {
    const { pwaStatus, deferredPwaPrompt, pwaPromptAttempt, showInstallPrompt } =
        usePwaInstallPrompt(storageKey);

    const install = useMemo(
        () => ({
            pwaStatus,
            deferredPwaPrompt,
            pwaPromptAttempt,
            showInstallPrompt,
        }),
        [pwaStatus, deferredPwaPrompt, pwaPromptAttempt, showInstallPrompt]
    );

    if (enablePhase) {
        return (
            <PwaPhaseGate storageKey={storageKey} install={install}>
                {(contextValue) => <PwaContext value={contextValue}>{children}</PwaContext>}
            </PwaPhaseGate>
        );
    }

    return <PwaContext value={install}>{children}</PwaContext>;
};

export { PwaProvider, PwaContext };
